import React from 'react';
import { Box } from '@mui/material';
import { AssetSummaryExternal, AssetTypeSummaryExternal } from '@askporter/client-grieg-lyric';
import { useToast } from '@askporter/toast';
import { copyToClipboard } from '@askporter/utils';
import { AssetAvatar, BreadcrumbProps, Breadcrumbs, Card } from '../../';

export interface AssetListCardProps {
  assetRef?: string;
  ancestors?: Array<AssetSummaryExternal>;
  isLoading?: boolean;
  isFilterOption?: boolean;
  title?: string;
  uid?: string;
  postalCode?: string;
  assetType?: AssetTypeSummaryExternal;
  imagePath?: string;
  cardOnClick?: (uid: string) => void;
  isSmallDevice: boolean;
  plain?: boolean;
  t: (key: string, options?: Record<string, string | number>) => string;
  captureException?: (error: Error) => void;
  href?: string;
}

export const AssetListCard: React.FC<React.PropsWithChildren<AssetListCardProps>> = ({
  assetRef,
  ancestors = [],
  isLoading,
  isFilterOption,
  title,
  uid,
  postalCode,
  assetType,
  imagePath,
  cardOnClick,
  isSmallDevice,
  plain = false,
  t,
  // eslint-disable-next-line no-console
  captureException = console.error,
  href,
}: AssetListCardProps) => {
  const { showSuccess, showError } = useToast();

  const copyFunction = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();

    const isSuccess = copyToClipboard(`${window.location.origin}/app/assets/${uid}`, (...args: unknown[]) => {
      if (args[0] instanceof Error) {
        captureException(args[0]);
      }
    });
    const message = isSuccess
      ? t('ns.asset_list:asset_link:clipboard_success')
      : t('ns.asset_list:asset_link:clipboard_error');

    if (isSuccess) {
      showSuccess(message);
    } else {
      showError(message);
    }

    return message;
  };
  const assetRefBreadcrumb: BreadcrumbProps[] = assetRef ? [{ label: assetRef }] : [];
  const breadcrumbs = [
    ...assetRefBreadcrumb,
    ...ancestors?.map((ancestor) => {
      return { label: ancestor?.title };
    }),
  ]
    .reverse()
    .filter(Boolean);

  return (
    <Card
      onClick={cardOnClick && uid ? () => cardOnClick(uid) : undefined}
      dataTestId={`${uid || ''}-asset-list-card`}
      plain={plain}
      href={href ? href : undefined}
    >
      {!isFilterOption && breadcrumbs?.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Breadcrumbs
            items={breadcrumbs}
            onCopy={isLoading ? undefined : copyFunction}
            copyAriaLabel={t?.('ns.asset_list:asset_link:aria_label') || 'Copy asset link'}
          />
        </Box>
      )}
      <AssetAvatar
        title={title || ''}
        assetRef={assetRef || ''}
        assetType={assetType}
        postalCode={postalCode}
        imagePath={imagePath}
        uid={uid}
        isLoading={isLoading}
        isFilterOption={isFilterOption}
        isSmallDevice={isSmallDevice}
      />
    </Card>
  );
};
